import { computed, inject, Injectable, Injector, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MbsSize } from 'mbs-ui-kit';
import { getGeneralHostInfo, HostInfoRow } from 'mbs-ui-shared@components/computers-shared/get-general-host-Info';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { AvailablePlanStartModes } from 'mbs-ui-shared@models/backup/available-plan-start-modes';
import { AvailablePlanTypes } from 'mbs-ui-shared@models/backup/available-plan-types';
import { DiagnosticLog } from 'mbs-ui-shared@models/backup/diagnostic-log-model';
import {
  KvmMachineAction,
  KvmMachineDeleteParams,
  KvmMachineRdpConsoleOpenParams,
  KvmMachineRdpConsoleOpenResult,
  KvmMachineRdpSessionsData,
  KvmMachineScreenshot,
  KvmMachineScreenshotParams,
  KvmMachinesData,
  KvmMachineStartParams,
  KvmMachineStopParams
} from 'mbs-ui-shared@models/backup/kvm-machine';
import { PlanHistoryData, PlanHistoryDetails } from 'mbs-ui-shared@models/backup/plan-history-model';
import { PlanInfo, PlanInfoData } from 'mbs-ui-shared@models/backup/plan-info-model';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import { PlanMode, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RemoteCommandResponse, RemovePlansWithDataReturnType } from 'mbs-ui-shared@models/rm/remote-command-model';
import { StorageAccountV1 } from 'mbs-ui-shared@models/StorageAccount';
import { ComputersAbstractService } from 'mbs-ui-shared@services/computers.service';
import { RmCommandsWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import moment from 'moment';
import { map, Observable } from 'rxjs';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { PlansWizardMode } from 'wizards/models/base/base-plan-models';
import { RestoreIbbTypeModel } from 'wizards/models/restoreIBB/restore-ibb-type-model';
import { IbbRestoreTypeWizardComponent } from 'wizards/remote-management-wizards/ibb-restore-type-wizard/ibb-restore-type-wizard.component';
import { RemoteManagementWizardsService, WizardRestoreSettings, WizardSettings } from 'wizards/services/remote-management-wizards.service';
import { ComputersBackupStore } from '../store/computersBackup/computersBackup.store';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ComputerBackupFacade {
  private computersBackupStore = inject(ComputersBackupStore);
  private rmCommands = inject(RmCommandsWrapper);
  private wizardService = inject(RemoteManagementWizardsService);
  private computersService = inject(ComputersAbstractService);
  private computersFacade = inject(ComputersFacade);
  private injector = inject(Injector);

  public consistencyCheckMap: { [key: string]: boolean };

  private entityByHid = (hid: string) => this.computersBackupStore.entityByHid(hid);

  // host info
  public isHostInfoByHidExist = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.hostInfo?.data);
  public hostInfoByHidLoading = (hid: string): Signal<boolean> =>
    computed(() => this.entityByHid(hid)()?.hostInfo?.visibleLoading ?? false);
  public hostInfoByHidLoaded = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.hostInfo?.loaded);
  public hostInfoByHid = (hid: string): Signal<HostInfoRow[]> => computed(() => this.entityByHid(hid)()?.hostInfo?.data ?? []);

  public hostInfoByHidDirect$ = (hid: string): Observable<HostInfoRow[]> =>
    this.computersService.getComputerHostSystemInfo(hid).pipe(
      map((data) =>
        data && !data.message ? getGeneralHostInfo(data, true, hid).filter((result) => (result.visible ? result.visible() : true)) : []
      ),
      tap((data) => this.computersBackupStore.setGeneralInfoSuccess(hid, data))
    );

  // destinations
  public isDestinationByHidExist = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.destinations?.data);
  public destinationsByHidLoading = (hid: string): Signal<boolean> =>
    computed(() => this.entityByHid(hid)()?.destinations?.visibleLoading ?? false);
  public destinationsByHidLoaded = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.destinations?.loaded);
  public destinationsByHid = (hid: string): Signal<StorageAccountV1[]> => computed(() => this.entityByHid(hid)()?.destinations?.data ?? []);

  // plans
  public isPlansByHidExist = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.plans?.data);
  public plansErrorByHid = (hid: string): Signal<Error | undefined> => computed(() => this.entityByHid(hid)()?.plans?.error);
  public plansByHidLoading = (hid: string): Signal<boolean> => computed(() => this.entityByHid(hid)()?.plans?.visibleLoading ?? false);
  public plansByHidLoaded = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.plans?.loaded);
  public plansByHid = (hid: string): Signal<PlanInfo[]> => computed(() => this.entityByHid(hid)()?.plans?.data ?? []);

  public isPlanRunningById = (id: string): Signal<boolean> =>
    computed(() => this.computersBackupStore.plansStatuses()[id]?.running ?? false);
  public isPlanSavingById = (id: string): Signal<boolean> => computed(() => this.computersBackupStore.plansStatuses()[id]?.saving ?? false);

  // available plans
  public isAvailablePlansByHidExist = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.availablePlans?.data);
  public availablePlansByHidLoading = (hid: string): Signal<boolean> =>
    computed(() => this.entityByHid(hid)()?.availablePlans?.visibleLoading ?? false);
  public availablePlansLoadedByHid = (hid: string): Signal<boolean> => computed(() => !!this.entityByHid(hid)()?.availablePlans?.loaded);
  public availablePlansByHid = (hid: string): Signal<AvailablePlanTypes> =>
    computed(() => this.entityByHid(hid)()?.availablePlans?.data ?? []);

  // sync process & timezone
  public syncProcessByHid = (hid: string): Signal<boolean> => computed(() => this.entityByHid(hid)()?.syncProcess ?? false);
  public timezoneByHid = (hid: string): Signal<number> => computed(() => this.entityByHid(hid)()?.timeZoneOffset ?? 0);

  public clear(): void {
    this.computersBackupStore.clear();
  }

  public loadGeneralInfo(options: { hid: string; force?: boolean; quiet?: boolean }): void {
    this.computersBackupStore.loadGeneralInfo(options);
  }

  public loadDestinations(options: { hid: string; force?: boolean; quiet?: boolean; doNotIncludeLocal?: boolean }): void {
    this.computersBackupStore.loadDestinations(options);
  }

  public loadPlans(options: { computer: Computer; force?: boolean; quiet?: boolean }): void {
    this.computersBackupStore.loadPlans(options);
  }

  public getPlansHistory(
    hid: string,
    params: { PlanId?: string; Limit?: number; Offset?: number; FromUtc?: string; ToUtc?: string; IsAsync?: boolean }
  ): Observable<{ items: Array<PlanHistoryData>; timeZoneOffset: number; totalCount: number }> {
    return this.rmCommands.getPlansHistory(hid, params);
  }

  public getPlanHistoryDetails(
    hid: string,
    params: { SessionId: string; Year: number; Month: number; IsAsync?: boolean }
  ): Observable<PlanHistoryDetails> {
    return this.rmCommands.getPlanHistoryDetails(hid, params);
  }

  public getBackupAgentLog(hid: string, params: Record<string, string | number | boolean>): Observable<DiagnosticLog> {
    return this.rmCommands.getBackupAgentLog(hid, params);
  }

  public getComputerPlans(hid: string): Observable<PlanInfoData> {
    return this.rmCommands.getComputerPlans(hid);
  }

  public getPlanInfo(hid: string, planId: string): Observable<PlanInfo> {
    return this.rmCommands.getPlanInfo(hid, planId);
  }

  public getComputerVirtualMachines(hid: string): Observable<KvmMachinesData> {
    return this.rmCommands.getComputerVirtualMachines(hid);
  }

  public startKvmMachine(hid: string, params: KvmMachineStartParams): Observable<KvmMachineAction> {
    return this.rmCommands.startKvmMachine(hid, params);
  }

  public stopKvmMachine(hid: string, params: KvmMachineStopParams): Observable<KvmMachineAction> {
    return this.rmCommands.stopKvmMachine(hid, params);
  }

  public deleteKvmMachine(hid: string, params: KvmMachineDeleteParams): Observable<KvmMachineAction> {
    return this.rmCommands.deleteKvmMachine(hid, params);
  }

  public getRdpMachineConsoles(hid: string): Observable<KvmMachineRdpSessionsData> {
    return this.rmCommands.getRdpMachineConsoles(hid);
  }

  public openRdpMachineConsole(hid: string, params: KvmMachineRdpConsoleOpenParams): Observable<KvmMachineRdpConsoleOpenResult> {
    return this.rmCommands.openRdpMachineConsole(hid, params);
  }

  public closeRdpMachineConsole(hid: string, sessionId: string): Observable<boolean> {
    return this.rmCommands.closeRdpMachineConsole(hid, sessionId);
  }

  public getKvmMachineScreenshot(hid: string, params: KvmMachineScreenshotParams): Observable<KvmMachineScreenshot> {
    return this.rmCommands.getKvmMachineScreenshot(hid, params);
  }

  public getServiceAccountName(computer: Computer, agentType: AgentType = AgentType.Backup): Observable<string> {
    return this.rmCommands.getServiceAccountName(computer, agentType);
  }

  public changeServiceAccountName(
    computer: Computer,
    account: { accountName: string; accountPassword?: string },
    agentType: AgentType = AgentType.Backup
  ): Observable<boolean> {
    return this.rmCommands.changeServiceAccountName(computer, account, agentType);
  }

  public startPlan(computer: Computer, id: string, mode: AvailablePlanStartModes): Observable<boolean> {
    return this.rmCommands.startPlan(computer, id, mode);
  }

  public stopPlan(computer: Computer, id: string): Observable<boolean> {
    return this.rmCommands.stopPlan(computer, id);
  }

  public removePlan(computer: Computer, id: string): Observable<boolean> {
    return this.rmCommands
      .removePlan(computer, id)
      .pipe(tap(() => this.computersFacade.loadComputerByHid({ hid: computer.hid, force: true })));
  }

  public removePlanWithData(computer: Computer, ids: string[]): Observable<RemoteCommandResponse<RemovePlansWithDataReturnType>> {
    return this.rmCommands
      .removePlansWithData(computer, ids)
      .pipe(tap(() => this.computersFacade.loadComputerByHid({ hid: computer.hid, force: true })));
  }

  public clonePlansFromComputer(sourceHid: string, destinationHid: string, plansId: string[]): Observable<null> {
    return this.computersService.clonePlansFromComputer(sourceHid, destinationHid, plansId);
  }

  public clonePlan(computer: Computer, id: string, name: string): Observable<boolean> {
    return this.rmCommands
      .clonePlan(computer, id, name)
      .pipe(tap(() => this.computersFacade.loadComputerByHid({ hid: computer.hid, force: true })));
  }

  public saveLegacyPlan(plan, settings = null): Observable<any> {
    const planParam = this.wizardService.PreparePlanBeforePostRM(plan);
    this.setPlanSaving(planParam.ID, true);
    return this.wizardService.PostRMPlan(plan, settings).pipe(
      finalize(() => {
        this.setPlanSaving(planParam.ID, false);
        this.wizardService.hid && this.computersFacade.loadComputerByHid({ hid: this.wizardService.hid, force: true });
      })
    );
  }

  public getLegacyPlan(settings = null, extended = false): Observable<any> {
    return extended ? this.wizardService.GetRMPlanWithAdditionalRequests() : this.wizardService.GetRMPlanModel(settings);
  }

  public getAgentUtcOffsetValue(hid: string): Observable<number> {
    return toObservable(this.isHostInfoByHidExist(hid), { injector: this.injector }).pipe(
      switchMap((exist) => (exist ? toObservable(this.hostInfoByHid(hid), { injector: this.injector }) : this.hostInfoByHidDirect$(hid))),
      map((data) => {
        const timeZone = (data || []).find((hostInfo) => hostInfo.key === 'Time zone');
        if (!timeZone?.value) return 0;
        const regexp = /^\(UTC([+-]*2[0-3]|[+-][01][0-9]:[0-5][0-9])\)/;
        const regexpArr = regexp.exec(timeZone.value);
        const utcOffsetValue = regexpArr?.length ? regexpArr[1] : null;
        return utcOffsetValue ? moment().utcOffset(utcOffsetValue).utcOffset() : null;
      })
    );
  }

  public loadAvailablePlanTypes(options: { computer: Computer; force?: boolean }): void {
    this.computersBackupStore.loadAvailablePlans(options);
  }

  public setPlanSaving(id: string, saving = true): void {
    this.computersBackupStore.setPlanSaving({ id, saving });
  }

  public setPlanRunning(id: string, running = true): void {
    this.computersBackupStore.setPlanRunning({ id, running });
  }

  public loadSyncStatus(hid: string): void {
    this.computersBackupStore.loadSyncStatus(hid);
  }

  public restoreIbbWithWizard(computer: Computer, params: WizardRestoreSettings): Observable<RestoreIbbTypeModel> {
    const newParams: WizardSettings = {
      hid: computer.hid,
      mode: PlanMode.create,
      wizardMode: PlansWizardMode.forRMM,
      planId: '',
      isNBF: true,
      isOffline: !computer.online,
      type: PlanTypes.RestoreDiskImagePlan,
      isNetBuild: Computer.isNetBuild(computer),
      isUnix: Computer.isUnix(computer),
      isLinux: Computer.isLinux(computer),
      isMac: Computer.isMac(computer),
      compName: Computer.getComputerName(computer),
      compVmName: Computer.getComputerVmName(computer),
      email: computer?.userAccount?.accountName || null,
      backupVersion: Computer.getAgent(computer, AgentType.Backup)?.version || null,
      userId: computer?.userAccount?.id || null,
      restoreParams: params
    } as WizardSettings;

    return this.wizardService.setWizard(IbbRestoreTypeWizardComponent, { size: MbsSize.lg }, newParams).afterClosed.pipe(
      first(),
      map((res: string) => {
        if (res !== 'Save') return null;

        const model = { ...this.wizardService.restoreIbbTypeModel };
        this.wizardService.restoreIbbTypeModel = null;

        return model;
      })
    );
  }
}
